import { CustomSize, UndoRedoStep } from "Interfaces";
import React from "react";
import create from "zustand";
import update from "immutability-helper";
import arImg from "./assets/images/ar_icon.png";

interface Store {
  isViewerMode: boolean;
  isDraftEditor: boolean;
  isEditorMode: boolean;
  isMobile: boolean;
  setIsMobile: (isMobile: boolean) => void;
  dialogs: { id: string; dialog: React.ReactElement }[];
  addDialog: (id: string, dialog: React.ReactElement) => void;
  removeDialog: (id: string) => void;
  selectedGroupId: number | null;
  selectedAttributeId: number | null;
  selectedStepId: number | null;
  setSelectedGroupId: (group: number | null) => void;
  setSelectedAttributeId: (attribute: number | null) => void;
  setSelectedStepId: (step: number | null) => void;
  isLoading: boolean;
  setIsLoading: (isLoading: boolean) => void;
  isQuoteLoading: boolean;
  setIsQuoteLoading: (isQuoteLoading: boolean) => void;
  priceFormatter: Intl.NumberFormat;
  setPriceFormatter: (priceFormatter: Intl.NumberFormat) => void;
  undoStack: UndoRedoStep[] | any[any];
  setUndoStack: (
    callback: (
      undoStack: UndoRedoStep[] | any[any]
    ) => UndoRedoStep[] | any[any]
  ) => void;
  redoStack: UndoRedoStep[] | any[any];
  setRedoStack: (
    callback: (
      redoStack: UndoRedoStep[] | any[any]
    ) => UndoRedoStep[] | any[any]
  ) => void;
  isUndo: boolean;
  setIsUndo: (isUndo: boolean) => void;
  isRedo: boolean;
  setIsRedo: (isRedo: boolean) => void;
  personalDetailsData: CustomSize[];
  initializePersonalDetailsData: (personalDetailsData: CustomSize[]) => void;
  setPersonalDetailsData: (label: string, value: string) => void;
  customSizes: CustomSize[];
  setInitializeCustomSizes: (customSizes: CustomSize[]) => void;
  setCustomSizes: (label: string, value: string) => void;
}

const useStore = create<Store>((set, get) => ({
  isViewerMode: new URLSearchParams(window.location.href).get("viewerMode")
    ? true
    : false,
  isDraftEditor: new URLSearchParams(window.location.href).get("isDraftEditor")
    ? true
    : false,
  isEditorMode: new URLSearchParams(window.location.href).get("editorMode")
    ? true
    : false,
  isMobile: window.innerWidth <= 1024,
  setIsMobile: (isMobile: boolean) => {
    set({
      isMobile,
    });
  },
  dialogs: [],
  addDialog: (id: string, dialog: React.ReactElement) => {
    set((state) => ({
      dialogs: [...state.dialogs, { id, dialog }],
    }));
  },
  removeDialog: (id: string) => {
    set((state) => ({
      dialogs: state.dialogs.filter((dialog) => dialog.id !== id),
    }));
  },

  isLoading: false,
  setIsLoading: (isLoading: boolean) => {
    set({
      isLoading,
    });
  },
  isQuoteLoading: false,
  setIsQuoteLoading: (isQuoteLoading: boolean) => {
    set({
      isQuoteLoading,
    });
  },
  selectedGroupId: null,
  setSelectedGroupId: (groupId) =>
    set({
      selectedGroupId: groupId,
    }),
  selectedAttributeId: null,
  setSelectedAttributeId: (attributeId) =>
    set({
      selectedAttributeId: attributeId,
    }),
  selectedStepId: null,
  setSelectedStepId: (stepId) =>
    set({
      selectedStepId: stepId,
    }),
  priceFormatter: new Intl.NumberFormat("it-IT", {
    style: "currency",
    currency: "EUR",
  }),
  setPriceFormatter: (priceFormatter: Intl.NumberFormat) => {
    set({
      priceFormatter,
    });
  },
  undoStack: [],
  setUndoStack: (callback) => {
    set((prev) => ({
      undoStack: callback(prev.undoStack),
    }));
  },
  redoStack: [],
  setRedoStack: (callback) => {
    set((prev) => ({
      redoStack: callback(prev.redoStack),
    }));
  },
  isUndo: false,
  setIsUndo: (isUndo: boolean) => {
    set({
      isUndo,
    });
  },
  isRedo: false,
  setIsRedo: (isRedo: boolean) => {
    set({
      isRedo,
    });
  },
  personalDetailsData: [],
  initializePersonalDetailsData: (personalDetailsData: CustomSize[]) => {
    set({
      personalDetailsData,
    });
  },
  setPersonalDetailsData: (label: string, value: string) => {
    const index = get().personalDetailsData.findIndex((x) => x.label === label);

    if (index !== -1) {
      set((prev) =>
        update(prev, {
          personalDetailsData: {
            [index]: {
              value: {
                $set: value,
              },
            },
          },
        })
      );
    }
  },
  customSizes: [],
  setInitializeCustomSizes: (customSizes: CustomSize[]) => {
    set({
      customSizes,
    });
  },
  setCustomSizes: (label: string, value: string) => {
    const index = get().customSizes.findIndex((x) => x.label === label);
    if (index !== -1) {
      set((prev) =>
        update(prev, {
          customSizes: {
            [index]: {
              value: {
                $set: value,
              },
            },
          },
        })
      );
    }
  },
}));

export default useStore;
